import React, { useEffect, useState } from "react";
import {
  BankOutlined,
  BarChartOutlined,
  FileTextOutlined,
  InboxOutlined,
  LogoutOutlined,
  MailOutlined,
  MenuOutlined,
  NotificationOutlined,
  QuestionCircleOutlined,
  SafetyOutlined,
  SettingOutlined,
  TagOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { Avatar, Drawer, Menu, Popover } from "antd";
import logo from "../../assets/Logo/logo_validation.png";
import "./index.css";
import { useAuth } from "oidc-react";
import { useJwt } from "react-jwt";
import {
  convertToBoolean,
  userInitial,
  userInitialBackgroundColor,
} from "../../utils/string.utils";
import OfficeAccessModal from "../popups/Header/Profile/OfficeAccessModal";

const { SubMenu } = Menu;

const Header = ({ admin, adminData }) => {
  const location = useLocation();
  const { REACT_APP_SETTINGS_SHOW_LOGO } = process.env;

  const { userData, userManager } = useAuth();
  const [showDrawer, setShowDrawer] = useState(false);
  const [isOfficeAccessModalOpen, setIsOfficeAccessModalOpen] = useState(false);
  const [modalTabKey, setModalTabKey] = useState("offices");

  const token = userData?.access_token;
  const { decodedToken } = useJwt(token);
  const userName = decodedToken?.name;
  const userIdentity = userName || adminData?.email || "";
  const userInitials = userInitial(userIdentity);
  const userAvatarBg = userInitialBackgroundColor(userIdentity);
  const navigate = useNavigate();

  const officesCount = adminData?.offices?.length ?? 0;
  const fleetsCount = adminData?.fleets?.length ?? 0;
  const accountingCompaniesCount = adminData?.accountingCompanies?.length ?? 0;
  const responsibleVesselsCount =
    adminData?.vesselResponsibilities?.length ?? 0;

  useEffect(() => {
    const refreshInterval = setInterval(async () => {
      try {
        await userManager.signinSilent();
      } catch (_) {}
    }, 600000);
    return () => clearInterval(refreshInterval);
  }, [userManager]);

  useEffect(() => {
    userManager.signoutRedirectCallback().catch((error) => {
      console.error(
        "Error occurred while processing sign-out callback:",
        error,
      );
    });
  }, [userManager]);

  const signOut = async () => {
    try {
      await userManager.signoutRedirect();
    } catch (_) {}
  };

  const handleDrawerItemClick = (path) => {
    setShowDrawer(false);
    navigate(path);
  };

  const openAccessModal = (tabKey) => {
    setModalTabKey(tabKey);
    setIsOfficeAccessModalOpen(true);
  };

  const accessChips = [
    { label: "Offices", count: officesCount, key: "offices" },
    { label: "Fleets", count: fleetsCount, key: "fleets" },
    { label: "Entities", count: accountingCompaniesCount, key: "entities" },
    {
      label: "Vessel Responsibilities",
      count: responsibleVesselsCount,
      key: "vesselResponsibilities",
    },
  ];

  const isManager =
    (adminData?.isManager || adminData?.isVesselManager) ?? false;
  const showReports = admin || isManager;

  const workspaceItems = [
    {
      path: "/invoice",
      label: "Invoices",
      icon: <FileTextOutlined />,
      match: "/invoice",
    },
    {
      path: "/emailqueues",
      label: "Email Queues",
      icon: <InboxOutlined />,
      match: "/emailqueues",
    },
    ...(showReports
      ? [
          {
            path: "/report",
            label: "Reports",
            icon: <BarChartOutlined />,
            match: "/report",
          },
        ]
      : []),
    {
      path: "/user-guide",
      label: "User Guide",
      icon: <QuestionCircleOutlined />,
      match: "/user-guide",
    },
    {
      path: "/release-notes",
      label: "Release Notes",
      icon: <NotificationOutlined />,
      match: "/release-notes",
    },
  ];

  const adminItems = admin
    ? [
        {
          path: "/admin/permitted-emails",
          label: "Permitted Emails",
          icon: <SafetyOutlined />,
        },
        {
          path: "/admin/mapped-kvps",
          label: "Mapped KVPs",
          icon: <TagOutlined />,
        },
        {
          path: "/admin/document-labels-mapping",
          label: "Document Labels",
          icon: <TagOutlined />,
        },
        {
          path: "/admin/suppliers",
          label: "Suppliers",
          icon: <BankOutlined />,
        },
        {
          path: "/admin/mailbox-users",
          label: "Mailbox Users",
          icon: <MailOutlined />,
        },
        {
          path: "/admin/ocr-configuration",
          label: "OCR Configuration",
          icon: <SettingOutlined />,
        },
      ]
    : [];

  const isActive = (match) => location.pathname.startsWith(match);

  const profileContent = (
    <div className="w-full">
      <div className="mb-3 pb-3 border-b border-gray-100">
        <div className="text-xs text-gray-400 mb-0.5">Email Address</div>
        <div className="text-sm font-medium text-gray-800">
          {adminData?.email}
        </div>
        {(adminData?.isAdmin || isManager) && (
          <span
            className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full text-white font-medium"
            style={{ backgroundColor: "var(--primary-color)" }}
          >
            {adminData.isAdmin ? "Admin" : "Manager"}
          </span>
        )}
      </div>
      <div className="text-xs text-gray-400 mb-2">Access</div>
      <div className="flex flex-wrap gap-1.5">
        {accessChips.map(({ label, count, key }) => (
          <span
            key={key}
            onClick={() => openAccessModal(key)}
            className="cursor-pointer text-xs px-2.5 py-1 rounded-full text-white font-medium transition-opacity hover:opacity-80"
            style={{ backgroundColor: "rgb(7, 126, 140)" }}
          >
            {label} ({count})
          </span>
        ))}
      </div>
      <div
        onClick={signOut}
        className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 text-sm text-gray-600 cursor-pointer hover:text-red-500 transition-colors"
      >
        <LogoutOutlined />
        Sign out
      </div>
    </div>
  );

  const brandBlock = (
    <div className="flex items-center gap-2.5 px-5 py-5 border-b border-white/5">
      <div
        className="w-9 h-9 rounded-lg grid place-items-center font-bold text-base"
        style={{
          background:
            "linear-gradient(135deg, var(--secondary-color) 0%, var(--secondary-color) 100%)",
          color: "var(--primary-color)",
          boxShadow: "0 4px 12px rgba(95,198,106,.25)",
        }}
      >
        VS
      </div>
      <div className="leading-tight">
        <div
          className="font-amina text-[16px]"
          style={{ color: "var(--secondary-color)" }}
        >
          ValidationStation
        </div>
        {convertToBoolean(REACT_APP_SETTINGS_SHOW_LOGO) && (
          <div
            className="flex items-center gap-1 text-[10px] opacity-70"
            style={{ color: "var(--secondary-color)" }}
          >
            <span>Part of</span>
            <img src={logo} alt="" className="h-3" />
          </div>
        )}
      </div>
    </div>
  );

  const navItem = ({ path, label, icon }) => (
    <div
      key={path}
      onClick={() => navigate(path)}
      className={`sidebar-nav-item ${isActive(path) ? "active" : ""}`}
    >
      <span className="sidebar-nav-icon">{icon}</span>
      <span className="flex-1">{label}</span>
    </div>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div
        className="sm:hidden fixed top-0 left-0 right-0 h-14 z-20 flex items-center justify-between px-4"
        style={{ backgroundColor: "var(--primary-color)" }}
      >
        <MenuOutlined
          className="text-xl cursor-pointer"
          style={{ color: "var(--secondary-color)" }}
          onClick={() => setShowDrawer(true)}
        />
        <div
          className="font-amina text-base"
          style={{ color: "var(--secondary-color)" }}
        >
          ValidationStation
        </div>
        <Popover
          placement="bottomRight"
          overlayClassName="custom-popover-width"
          overlayStyle={{ maxWidth: "calc(100vw - 16px)" }}
          content={profileContent}
          trigger="click"
          autoAdjustOverflow={true}
          arrow={{ pointAtCenter: true }}
        >
          <Avatar
            size="small"
            icon={<UserOutlined />}
            className="cursor-pointer"
            style={{ backgroundColor: "var(--secondary-color)" }}
          />
        </Popover>
      </div>

      {/* Sidebar (desktop) */}
      <aside
        className="hidden sm:flex flex-col w-[248px] flex-shrink-0 sticky top-0 h-screen"
        style={{ backgroundColor: "var(--primary-color)", color: "#CFE0DE" }}
      >
        {brandBlock}

        <div className="flex-1 overflow-y-auto py-3">
          <div className="px-5 pb-1.5 text-[11px] font-semibold tracking-wider uppercase opacity-50">
            Workspace
          </div>
          <div className="px-2.5">{workspaceItems.map(navItem)}</div>

          {adminItems.length > 0 && (
            <>
              <div className="px-5 pt-4 pb-1.5 text-[11px] font-semibold tracking-wider uppercase opacity-50">
                Admin
              </div>
              <div className="px-2.5">{adminItems.map(navItem)}</div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2.5 px-4 py-3 border-t border-white/5">
          <Avatar
            size={32}
            style={{
              backgroundColor: userAvatarBg,
              color: "#fff",
              fontWeight: 600,
            }}
          >
            {userInitials || <UserOutlined />}
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-white truncate">
              {userName || adminData?.email}
            </div>
            {(adminData?.isAdmin || isManager) && (
              <div className="text-[11px] opacity-60">
                {adminData?.isAdmin ? "Admin" : "Manager"}
              </div>
            )}
          </div>
          <LogoutOutlined
            className="opacity-60 hover:opacity-100 cursor-pointer"
            onClick={signOut}
            title="Sign out"
          />
        </div>
      </aside>

      {/* Mobile drawer */}
      <Drawer
        title={
          <div
            className="font-amina text-[18px]"
            style={{ color: "var(--primary-color)" }}
          >
            ValidationStation
          </div>
        }
        placement="left"
        onClose={() => setShowDrawer(false)}
        open={showDrawer}
      >
        <Menu
          mode="inline"
          onClick={() => setShowDrawer(false)}
          selectable={false}
        >
          {workspaceItems.map((item) => (
            <Menu.Item
              key={item.path}
              icon={item.icon}
              onClick={() => handleDrawerItemClick(item.path)}
            >
              {item.label}
            </Menu.Item>
          ))}
          {adminItems.length > 0 && (
            <SubMenu key="admin" title="Admin">
              {adminItems.map((item) => (
                <Menu.Item
                  key={item.path}
                  icon={item.icon}
                  onClick={() => handleDrawerItemClick(item.path)}
                >
                  {item.label}
                </Menu.Item>
              ))}
            </SubMenu>
          )}
        </Menu>
      </Drawer>

      {isOfficeAccessModalOpen && (
        <OfficeAccessModal
          visible={isOfficeAccessModalOpen}
          data={adminData}
          tabKey={modalTabKey}
          onCancel={() => setIsOfficeAccessModalOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
